import React, { useEffect, useState, useCallback } from "react";
import { Upload, Camera } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "./ui/kit";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";
import { stavUhrady, NahratFakturu, TlacitkoNahrat } from "./FakturyPripadu";

const pct = (n) => `${String(n).replace(".", ",")} %`;
const znamenko = (n) => `${n < 0 ? "−" : ""}${fmtKc(Math.abs(n))}`;

/**
 * Ekonomika technologie: přijaté faktury (materiál, subdodávky, projekt) jdou
 * nahrát kdykoli — i před nabídkou. Plán z nabídky a marže se ukážou po
 * schválení. Vydané faktury klientovi patří do ekonomiky případu.
 */
export function TechEkonomika({ techId, pripadId }) {
  const nav = useNavigate();
  const [d, setD] = useState(undefined);
  const [err, setErr] = useState("");
  const [nahrat, setNahrat] = useState(null);   // vybrané nebo přetažené soubory
  const [tazeni, setTazeni] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/technologie/${techId}/ekonomika`); setD(data); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); setD(null); }
  }, [techId]);
  useEffect(() => { load(); }, [load]);

  const zaplaceno = async (x) => {
    if (!window.confirm(`Zapsat platbu ${fmtKc(x.zbyva_uhradit)} Kč s dnešním datem?`)) return;
    try {
      await api.post(`/api/doklady/${x.id}/platby`, { datum: new Date().toISOString().slice(0, 10), castka: x.zbyva_uhradit });
      load();
    } catch (e) { window.alert(apiError(e.response?.data?.detail) || e.message); }
  };

  if (d === undefined) return <div className="card p-8 flex justify-center"><Spinner /></div>;
  if (d === null) return <div className="card p-6 t-telo text-danger">{err || "Nepodařilo se načíst ekonomiku."}</div>;

  const odchylky = (d.prijate_polozky || []).filter((p) => p.odchylka_pct != null || p.podceneni);
  const kf = d.k_fakturaci;

  return (
    <div className="space-y-3" data-testid="tech-ekonomika">
      {d.odemceno ? <PlanSkutecnost d={d} /> : (
        <div className="card px-4 md:px-5 py-3.5 flex items-center gap-3" data-testid="ekonomika-bez-planu">
          <span className="min-w-0 flex-1 t-meta text-muted">{d.duvod}</span>
          <span className="t-meta text-muted shrink-0">Náklady</span>
          <span className="amount t-telo font-800 text-ink shrink-0">{fmtKc(d.naklady)} Kč</span>
        </div>
      )}

      <div className={`card overflow-hidden ${tazeni ? "ring-2 ring-accent" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setTazeni(true); }} onDragLeave={() => setTazeni(false)}
        onDrop={(e) => { e.preventDefault(); setTazeni(false); const f = Array.from(e.dataTransfer.files || []); if (f.length) setNahrat(f); }}>
        <div className="px-4 md:px-5 pt-4 pb-2.5 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="t-sekce font-700 text-ink">Přijaté faktury</h3>
            <p className="t-meta text-muted mt-0.5">Materiál, subdodávky, projekty. Faktura se přečte sama a přiřadí k téhle technologii. Částky bez DPH.</p>
          </div>
          <TlacitkoNahrat onSoubory={setNahrat} testid="ekonomika-nahrat" className="hidden md:inline-flex btn-primary shrink-0">
            <Upload size={15} /> Nahrát fakturu
          </TlacitkoNahrat>
        </div>
        <div className="px-4 pb-3 md:hidden">
          <TlacitkoNahrat onSoubory={setNahrat} testid="ekonomika-nahrat-mobil"
            className="w-full h-12 rounded-pill bg-accent text-white t-telo font-700 inline-flex items-center justify-center gap-2">
            <Camera size={16} /> Vyfotit nebo nahrát fakturu
          </TlacitkoNahrat>
        </div>

        {d.prijate.map((x) => {
          const s = stavUhrady(x);
          return (
            <div key={x.id} className="px-4 md:px-5 py-3 border-t border-line-soft flex items-center gap-3" data-testid={`prijata-${x.id}`}>
              <span className="h-[7px] w-[7px] rounded-full shrink-0" style={{ background: s.barva }} aria-hidden="true" />
              <button onClick={() => nav(`/doklady/${x.id}`)} className="min-w-0 flex-1 text-left">
                <span className="block t-telo font-600 text-ink truncate">{x.dodavatel || "Dodavatel nepřečten"}</span>
                <span className="block t-meta text-muted truncate">
                  {[x.druh === "DOBROPIS" ? "dobropis" : null, x.cislo || "bez čísla"].filter(Boolean).join(" · ")}
                </span>
                <span className={`block t-meta ${s.cls}`}>{s.text}</span>
                {(x.duplicita_id || (d.odemceno && x.drazsi_nez_nabidka > 0)) && (
                  <span className="flex flex-wrap gap-x-3 t-meta">
                    {x.duplicita_id && <span className="text-danger">možná duplicita</span>}
                    {d.odemceno && x.drazsi_nez_nabidka > 0 && (
                      <span className="text-accent-hover">{x.drazsi_nez_nabidka === 1 ? "1 položka dráž" : `${x.drazsi_nez_nabidka} položky dráž`} než v nabídce</span>
                    )}
                  </span>
                )}
              </button>
              {x.ceka_na_platbu && (
                <button onClick={() => zaplaceno(x)} data-testid={`prijata-zaplaceno-${x.id}`}
                  className="tap md:min-h-0 md:min-w-0 h-[30px] px-3 rounded-pill border border-line-strong bg-white t-meta font-600 text-ink shrink-0">Zaplaceno</button>
              )}
              <span className="amount t-telo font-600 text-ink text-right shrink-0 md:w-[96px]">{fmtKc(x.castka)}</span>
            </div>
          );
        })}
        <div className="px-4 md:px-5 py-3 border-t border-line-soft flex items-center gap-3 text-muted">
          <span className="h-[7px] w-[7px] rounded-full shrink-0 bg-line" aria-hidden="true" />
          <span className="min-w-0 flex-1 t-telo">Materiál ze skladu (výdejky)</span>
          <span className="amount t-telo text-right shrink-0 md:w-[96px]">{fmtKc(d.sklad_kc)}</span>
        </div>
        {d.zalohy_dodavatelum?.pocet > 0 && (
          <div className="px-4 md:px-5 pb-3 t-meta text-muted">
            Zaplacené zálohy dodavatelům <span className="amount text-ink">{fmtKc(d.zalohy_dodavatelum.zaplaceno)} Kč</span> — náklad budou až s fakturou.
          </div>
        )}
        <TlacitkoNahrat onSoubory={setNahrat} testid="ekonomika-pretahni"
          className="hidden md:block w-[calc(100%-40px)] mx-5 mb-5 mt-2 border-[1.5px] border-dashed border-line-strong rounded-[12px] py-[18px] text-center t-meta text-muted hover:bg-inputbg">
          Přetáhni sem PDF nebo fotku faktury — klidně víc najednou
        </TlacitkoNahrat>

        {pripadId && (
          <div className="px-4 md:px-5 py-3 bg-[#fbfaf8] border-t border-line-soft flex flex-col md:flex-row md:items-center gap-1 md:gap-3 t-meta">
            <span className="flex-1 text-sec-3">
              {kf && kf.zbyva_bez_dph > 0
                ? <>Vyfakturovat klientovi zbývá <b className="amount text-ink">{fmtKc(kf.zbyva_bez_dph)} Kč</b> bez DPH — vydané faktury nahráváš v ekonomice případu.</>
                : "Vydané faktury klientovi nahráváš v ekonomice případu."}
            </span>
            <button onClick={() => nav(`/obchod/${pripadId}?zalozka=ekonomika`)} data-testid="ekonomika-pripadu-odkaz"
              className="tap md:min-h-0 text-left font-700 text-accent-hover hover:text-accent shrink-0">Ekonomika případu →</button>
          </div>
        )}
      </div>

      {odchylky.length > 0 && (
        <div className="card overflow-hidden" data-testid="ekonomika-odchylky">
          <div className="px-4 md:px-5 py-3 t-sekce font-700 text-ink">Ceny proti nabídce</div>
          {odchylky.map((p) => (
            <div key={p.id} className="px-4 md:px-5 py-2.5 border-t border-line-soft">
              <div className="flex items-center gap-3 t-telo">
                <span className="min-w-0 flex-1 truncate text-ink">{p.nazev}</span>
                <span className="hidden md:inline t-meta text-muted truncate max-w-[180px]">{p.protistrana || ""}</span>
                <span className="amount font-600 text-ink text-right shrink-0">{fmtKc(p.celkem)}</span>
                {p.odchylka_pct != null && (
                  <span className={`amount w-[64px] text-right font-700 shrink-0 ${p.odchylka_pct > 0 ? "text-danger" : "text-success"}`}>
                    {p.odchylka_pct > 0 ? "+" : ""}{pct(p.odchylka_pct)}
                  </span>
                )}
              </div>
              {p.podceneni && (
                <div className="mt-1.5 flex items-start gap-2 rounded-input bg-accent/[0.06] border border-accent/25 px-3 py-2" data-testid="podceneni-varovani">
                  <span className="h-2 w-2 rounded-full bg-accent mt-1 shrink-0" />
                  <span className="t-meta text-ink">Nákup je vyšší než tvoje prodejní cena z nabídky — tuhle položku prodáváš pod nákupem.</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {nahrat && (
        <NahratFakturu smer="PRIJATA" techId={techId} pocatecni={nahrat}
          onClose={() => setNahrat(null)} onHotovo={load} />
      )}
    </div>
  );
}

/** Plán z nabídky proti skutečnosti — počítač dvě karty, telefon jedna tabulka. */
function PlanSkutecnost({ d }) {
  const { plan, skutecnost: s } = d;
  // Skutečnost = co klient zaplatil minus co stály faktury — stejně jako v ekonomice případu.
  const vysledek = s.vyfakturovano - s.naklady;
  const barva = vysledek < 0 ? "text-danger" : vysledek > 0 ? "text-success" : "text-ink";
  const vysledekPct = s.vyfakturovano ? Math.round((vysledek / s.vyfakturovano) * 1000) / 10 : null;
  return (
    <div className="card p-4 md:p-5" data-testid="ekonomika-financni-blok">
      <div className="hidden md:grid grid-cols-2 gap-3">
        <div className="bg-surface rounded-[12px] px-4 py-3.5">
          <div className="label-up text-muted">Plán z nabídky</div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 mt-2 t-telo">
            <span className="text-sec-3">Prodej</span><span className="amount text-right">{fmtKc(plan.prodej)}</span>
            <span className="text-sec-3">Nákup</span><span className="amount text-right">{fmtKc(plan.nakup)}</span>
            <span className="font-700">Marže</span><span className="amount text-right font-700">{znamenko(plan.marze)} · {pct(plan.marze_pct)}</span>
          </div>
        </div>
        <div className="bg-surface rounded-[12px] px-4 py-3.5">
          <div className="label-up text-muted">Skutečnost</div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 mt-2 t-telo">
            <span className="text-sec-3">Vyfakturováno klientovi</span><span className="amount text-right">{fmtKc(s.vyfakturovano)}</span>
            <span className="text-sec-3">Náklady</span><span className="amount text-right">{fmtKc(s.naklady)}</span>
            <span className="font-700">Výsledek</span>
            <span className={`amount text-right font-700 ${barva}`}>{znamenko(vysledek)}{vysledekPct != null ? ` · ${pct(vysledekPct)}` : ""}</span>
          </div>
        </div>
      </div>
      <div className="md:hidden grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 gap-y-1.5 t-telo">
        <span /><span className="label-up text-muted text-right">Plán</span><span className="label-up text-muted text-right">Skutečnost</span>
        <span className="text-sec-3">Prodej</span><span className="amount text-right">{fmtKc(plan.prodej)}</span><span className="amount text-right">{fmtKc(s.vyfakturovano)}</span>
        <span className="text-sec-3">Náklady</span><span className="amount text-right">{fmtKc(plan.nakup)}</span><span className="amount text-right">{fmtKc(s.naklady)}</span>
        <span className="font-700">Marže</span><span className="amount text-right font-700">{znamenko(plan.marze)}</span>
        <span className={`amount text-right font-700 ${barva}`}>{znamenko(vysledek)}</span>
      </div>
    </div>
  );
}
